<script>
   /*************************************************************/
   /* Widget for baseline correction                             */
   /* -----------------------------------                       */
   /*************************************************************/

   import { Container, Select, Range, RangeDiscrete } from 'svelte-controls-basic';
   import PrepTemplate from './PrepTemplate.svelte';

   let {
      params = $bindable([0, 1, 0.1]),
      settings = $bindable(''),
      left,
      right,
      nvars = 0,
      ...others
   } = $props();

   // prefix for the label ids the controls below are named by, unique per widget instance
   const uid = $props.id();

   // constant parameters
   const title = 'Baseline correction';
   const typesMap = {'emsc': 0, 'als': 1};
   const types = Object.keys(typesMap);

   // the range a penalty is shown in: the decade above it, so a penalty on a power of ten is the
   // floor of its range and not its top, which the range control never reaches. it is held to the
   // ranges offered, so a value from outside them lands on the edge of the nearest one
   const penaltyRanges = ['1', '0.1', '0.01'];
   function penaltyRange(x) {
      const r = Math.pow(10, Math.floor(Math.log10(x)) + 1);
      return Math.min(1, Math.max(0.01, r)).toString();
   }


   // the type shown is the parameter read back through the map; the control writes the parameter
   // directly, so there is no copy of it to keep in step. the penalty range is the widget's own
   const type = $derived(types[params[0]]);
   let maxPenalty = $state(params[2] > 0 ? penaltyRange(params[2]) : '0.1');

   // the C side bounds the EMSC polynomial degree by the data the baseline is fitted on: the design
   // matrix is "nx_cut x (degree + 2)" and a wide one has no least squares solution, so the fit
   // refuses anything above "nx_cut - 2". this widget offered 0 to 5 whatever the data was, which
   // made a narrow trim - or simply a dataset with few variables - produce a model that fails to
   // fit with a generic error. "left" and "right" are the interval as of this method's position in
   // the chain, so they already account for any trim ahead of it and match what C calls "nx_cut"
   const maxDegree = $derived.by(() => {
      const n = Number.isFinite(right - left) ? right - left : nvars;
      return Math.max(0, Math.min(5, n - 2));
   });

   // derived settings
   const maxPenaltyNum = $derived(parseFloat(maxPenalty));
   const stepPenalty = $derived(maxPenaltyNum / 10);
   const decNumPenalty = $derived(Math.ceil(-Math.log10(maxPenaltyNum)) + 1);


   // effect to keep the penalty inside the range shown, for a changed range and for a chain
   // restored with the panel closed: the range control corrects a value itself, but only while it
   // is rendered. a value from outside lands on the nearest edge, as it does in the control
   $effect(() => {
      if (type !== 'als') return;
      const x = Number.isFinite(params[2]) ? params[2] : stepPenalty;
      const v = Math.min(Math.max(x, stepPenalty), maxPenaltyNum - stepPenalty);
      if (v !== params[2]) params[2] = v;
   });

   // effect to pull the degree down when the data cannot carry it. lowering the control's own "max"
   // is not enough on its own: "params[1]" arrives from a saved model or from a trim ahead of it
   // edited or moved, so it can already sit above the new bound. same reason the penalty is
   // corrected above, and guarded on the type for the same reason - "params[1]" is the ALS lambda
   // otherwise
   $effect(() => {
      if (type !== 'emsc') return;
      if (params[1] > maxDegree) params[1] = maxDegree;
   });

   // effect for making settings string
   $effect(() => {settings = type + ' (' + (type == 'emsc' ? params[1].toFixed(0) : params[1].toFixed(1) + ',' + params[2].toFixed(3))  + ')';});
</script>


<PrepTemplate {title} {settings} {...others}>
   <Container label="Type" id="{uid}-type">
   <Select bind:value={() => type, v => params[0] = typesMap[v]} options={types} />
   </Container>
   {#if type === "als"}
   <Container label="Lambda" id="{uid}-lambda">
   <Range bind:value={params[1]} min={1} max={7} step={0.1} />
   </Container>
   <Container label="Penalty range" id="{uid}-penaltyrange">
   <Select bind:value={maxPenalty} options={penaltyRanges} />
   </Container>
   <Container label="Penalty" id="{uid}-penalty">
   <Range bind:value={params[2]} min={stepPenalty} max={maxPenaltyNum - stepPenalty} step={stepPenalty} decNum={decNumPenalty}/>
   </Container>
   {/if}
   {#if type === "emsc"}
   <Container label="Polynomial" id="{uid}-polynomial">
   <RangeDiscrete bind:value={params[1]} min={0} max={maxDegree} step={1} />
   </Container>
   {/if}
</PrepTemplate>

